import React, { useCallback, useEffect, useMemo, useState } from "react";

import { last, uniqBy } from "lodash";
import { RouteComponentProps } from "react-router-dom";
import {
  JsonParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import useNormalizedSkillOptions from "../../hooks/useNormalizedSkillOptions";
import useTranslations from "../../hooks/useTranslations";

import { Tag, TagLabel, TagCloseButton } from "@chakra-ui/react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  H6,
  Link,
  ScreenContainer,
  ScreenHeader,
  Select,
} from "../wombat-ui";
import { AnalyticsIcon, ChevronDownIcon } from "../icons";

import Pagination from "../utils/Pagination";

import SkillEvolutionDetail from "./SkillEvolutionDetail";
import useViewer from "../../hooks/useViewer";
import SearchPopover from "../utils/SearchPopover";
import SearchPopoverHeading from "../utils/SearchPopoverHeading";
import SearchPopoverItem from "../utils/SearchPopoverItem";
import List from "../utils/List";
import UserAvatar from "../utils/avatars/UserAvatar";
import UserGradeHorizontalBarChart from "./charts/UserGradeHorizontalBarChart";
import useLastUserGradeChartData from "./hooks/useLastUserGradeChartData";
import useFuzzySearch from "../../hooks/useFuzzySearch";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { GroupFragment, SkillFragment, useGetAnswers, useGetAnswersLazyQuery, useGetUsersMinimal, useGetUsersMinimalLazyQuery, UserMinimalFragment } from '../../graphql/generated/definitions';

type SkillAnalyticsDetailProps = RouteComponentProps<{ id: string }>;

const SkillAnalyticsDetail: React.FC<SkillAnalyticsDetailProps> = ({
  match,
}) => {
  const t = useTranslations();
  const {
    company: { groups },
  } = useViewer();

  const { id: questionDigest } = match.params;

  const options = useNormalizedSkillOptions();

  const [{ groupSelection, selectedLevel }, setQuery] =
    useQueryParams({
      userId: StringParam,
      groupSelection: withDefault(JsonParam, []),
      selectedLevel: withDefault(NumberParam, last(options).value),
    });

  const [groupSearch, setGroupSearch] = useState("");

  const { data: answersData, loading: answersLoading } = useGetAnswers({
    variables: {
      questionDigest,
      lastCreatedAt: "now",
      groupIds: groupSelection.map(({ id }) => id)
    }
  });

  const answers = answersData?.answers?.collection || [];

  const referentAnswers = useMemo(() => (
    answers.filter(({ isCollaborator }) => !isCollaborator)
  ), [answers]);

  const filteredReferentAnswers = useMemo(() => (
    referentAnswers.filter(({ normalizedRating }) => normalizedRating > selectedLevel - 0.01 && normalizedRating < selectedLevel + 0.01)
  ), [referentAnswers, selectedLevel]);

  const collaborators = uniqBy(filteredReferentAnswers.map(({ collaborator }) => collaborator), 'id')

  const skill = useMemo(() => {
    return answers?.[0]?.question?.skill as SkillFragment;
  }, [answers]);

  useBreadcrumbs([
    {
      to: "/analytics/skill-mapping",
      text: t("statistics.skillMapping") as string,
    },
    {
      to: `/analytics/skills/${questionDigest}`,
      text: skill?.name,
    },
  ]);

  const chartData = useLastUserGradeChartData(
    answersData?.answers.collection,
    options
  );

  const handleGroupSelected = useCallback(
    ({ id, name }) => {
      setQuery({
        groupSelection: [...groupSelection, { id, name }],
      });
    },
    [groupSelection]
  );

  const filteredGroups = useMemo(() => {
    return (
      groups.filter(
        ({ id }) => !groupSelection.find(({ id: filteredId }) => filteredId === id)
      ) || []
    );
  }, [groups, groupSelection]);

  const searchFilteredGroup = useFuzzySearch(filteredGroups, groupSearch, {
    keys: ["name"],
    threshold: 0.3,
    useExtendedSearch: true,
    matchIfEmpty: true,
  }).map(({ item }) => item);

  return (
    <ScreenContainer>
      <ScreenHeader
        title={skill?.name}
        description={t("statistics.skill")}
        icon={AnalyticsIcon}
        actions={[
          <Link
            key={"back"}
            type="button"
            variant="button-subtle"
            to={"/analytics/skill-mapping"}
          >
            {t("header.goBack")}
          </Link>,
        ]}
      />

      <Container className="w-full">
        <div className="flex mt-6">
          <SearchPopover
            title={
              <SearchPopoverHeading>
                {t("collaborator.groups")}
              </SearchPopoverHeading>
            }
            loading={false}
            items={searchFilteredGroup}
            onItemSelected={handleGroupSelected}
            onSearchChange={setGroupSearch}
            searchValue={groupSearch}
            button={
              <Button variant="subtle" trailingIcon={ChevronDownIcon}>
                {t("statistics.filters.byGroups")}
              </Button>
            }
            noMatch={
              <SearchPopoverItem>
                {t("wombat.select.emptyResult")}
              </SearchPopoverItem>
            }
            renderItem={(item: GroupFragment) => (
              <SearchPopoverItem>{item.name}</SearchPopoverItem>
            )}
          />
        </div>
        <div className="flex gap-2 py-2">
          {groupSelection.map(({ id, name }) => (
            <Tag key={id}>
              <TagLabel>{name}</TagLabel>
              <TagCloseButton
                onClick={() =>
                  setQuery({
                    usersPage: 1,
                    usersByLevelPage: 1,
                    groupSelection: groupSelection.filter(
                      ({ id: groupId }) => id !== groupId
                    ),
                  })
                }
              />
            </Tag>
          ))}
        </div>
        <div className="gap-6 mt-6 mb-6 ">
          {
            chartData && (
              <Card className="">
                <CardBody className="">
                  <UserGradeHorizontalBarChart
                    data={chartData}
                    options={options}
                    onBarClick={(datum) =>
                      setQuery({
                        userId: datum?.userId,
                      })
                    }
                  />
                </CardBody>
              </Card>
            )
          }
          <Card className="mt-6 mb-6">
            <CardHeader>
              <p className="flex flex-wrap items-baseline gap-x-2">
                {t("statistics.collaboratorEvaluatedAsIn", {
                  as: (
                    <Select
                      id={"skill-options-selector"}
                      items={options}
                      defaultValue={options.find(
                        ({ value }) => value === selectedLevel
                      )}
                      renderItem={(item) => item.label}
                      getValue={(item) => item.value.toString()}
                      onChange={(item) =>
                        setQuery({
                          selectedLevel: item.value,
                        })
                      }
                      label={""}
                      srOnlyLabel
                    />
                  ),
                })}
              </p>
            </CardHeader>
            <CardBody className="flex flex-col flex-grow overflow-hidden">
              <div className="flex-1">
                <List
                  data={collaborators}
                  listOnly
                  isLoading={answersLoading}
                  emptyMessage={t("wombat.select.emptyResult") as string}
                  renderAsList={(user: UserMinimalFragment) => {
                    return (
                      <li className="py-4" key={user.id}>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center flex-1 min-w-0 space-x-2 ">
                            <UserAvatar
                              src={user.avatar}
                              size={8}
                              alt={`${user.name} profile picture`}
                            />
                            <Link to={`/users/${user.id}`} className="text-sm">
                              {user.name}
                            </Link>
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  id="user-mapping-list"
                />
              </div>
            </CardBody>
          </Card>
        </div>
        {
          skill && <SkillEvolutionDetail skillId={skill.id} />
        }
      </Container>
    </ScreenContainer>
  );
};

export default React.memo(SkillAnalyticsDetail);
